/**
 * The master refly list in Teslyn Cloud (R24, decision 503; docs/specs/project-reporting/refly-list.md §6). Staff read
 * the synced rows it is made from (line status, plan, flights, blocks, Geosoft QC results, lines files); a client reads
 * only the published snapshot (`refly_list_publication`, 20261025000600). RLS decides every read and write; these
 * functions only ask.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  Block,
  Flight,
  LineBadSection,
  ReflyFlag,
  LineStatus,
  LinesFileRecord,
  PlannedLine,
  QcLineResult,
  ReflyInput,
  ReflyRow,
  ReflyTick,
  ReflyTotals,
} from '@teslyn/domain';
import { all, type Res } from './api.ts';

export interface ReflyPublication {
  readonly id: string;
  readonly project_id: string;
  readonly title: string;
  readonly rows: ReflyRow[];
  readonly totals: ReflyTotals;
  readonly as_of: string;
  readonly published: boolean;
  readonly published_at: string | null;
  readonly withdrawn_at: string | null;
  readonly source: 'field' | 'cloud';
}

function must<T>(r: Res<T>): T {
  if (r.error) {
    const m = r.error.message;
    throw new Error(
      /row-level security|permission denied/i.test(m) ? 'You do not have permission to do this.' : m,
    );
  }
  return r.data as T;
}

const liveRows = <T>(sb: SupabaseClient, table: string, projectId: string, order: string) =>
  all<T>((from, to) =>
    sb
      .from(table)
      .select('*')
      .eq('project_id', projectId)
      .is('deleted_at', null)
      .order(order)
      .range(from, to),
  );

/** Staff: what the refly list is made from. */
export async function loadReflyInput(sb: SupabaseClient, projectId: string): Promise<ReflyInput> {
  const [blocks, plan, statuses, flights, qc, linesFiles, ticks, badSections, flags] = await Promise.all([
    liveRows<Block>(sb, 'block', projectId, 'name'),
    liveRows<PlannedLine>(sb, 'planned_line', projectId, 'number'),
    liveRows<LineStatus>(sb, 'line_status', projectId, 'id'),
    liveRows<Flight>(sb, 'flight', projectId, 'id'),
    liveRows<QcLineResult>(sb, 'qc_line_result', projectId, 'id'),
    liveRows<LinesFileRecord>(sb, 'lines_file', projectId, 'created_at'),
    liveRows<ReflyTick>(sb, 'refly_tick', projectId, 'created_at'),
    // decision 563: Diurnal QC's kept bad sections, a row each
    liveRows<LineBadSection>(sb, 'line_bad_section', projectId, 'updated_at'),
    // R127: the processor's refly flags, a row each with the zFids as flagged. Until 20261027100000 is applied the
    // table is not there: the list is shown as before, from the QC sections
    liveRows<ReflyFlag>(sb, 'refly_flag', projectId, 'created_at').catch((): ReflyFlag[] => []),
  ]);
  return { blocks, plan, statuses, flights, qc, linesFiles, ticks, badSections, flags };
}

/**
 * Tick or untick a row's "Checked" or "Added to aircraft" (decision 544; any staff member). A new row each time, so the
 * history stays; the database stamps who.
 */
export async function addReflyTick(
  sb: SupabaseClient,
  input: {
    readonly tenantId: string;
    readonly projectId: string;
    readonly rowKey: string;
    readonly lineStatusId: string | null;
    readonly tick: ReflyTick['tick'];
    readonly ticked: boolean;
  },
): Promise<ReflyTick> {
  const r = await sb
    .from('refly_tick')
    .insert({
      tenant_id: input.tenantId,
      project_id: input.projectId,
      row_key: input.rowKey,
      line_status_id: input.lineStatusId,
      tick: input.tick,
      ticked: input.ticked,
    })
    .select('*');
  const rows = must(r as Res<ReflyTick[]>);
  return rows[0]!;
}

const PUB_COLUMNS =
  'id, project_id, title, rows, totals, as_of, published, published_at, withdrawn_at, source';

/** The list the client sees: the published one (a client reads no other; staff filter the same). */
export async function loadPublishedRefly(
  sb: SupabaseClient,
  projectId: string,
): Promise<ReflyPublication | null> {
  const r = await sb
    .from('refly_list_publication')
    .select(PUB_COLUMNS)
    .eq('project_id', projectId)
    .eq('published', true)
    .order('created_at', { ascending: false })
    .limit(1);
  const rows = must(r as Res<ReflyPublication[]>);
  return rows[0] ?? null;
}

/** Publish the list as it is now (any staff member, decision 543; the database withdraws the one before). */
export async function publishRefly(
  sb: SupabaseClient,
  input: {
    readonly tenantId: string;
    readonly projectId: string;
    readonly title: string;
    readonly rows: readonly ReflyRow[];
    readonly totals: ReflyTotals;
    readonly asOf: string;
  },
): Promise<ReflyPublication> {
  const r = await sb
    .from('refly_list_publication')
    .insert({
      tenant_id: input.tenantId,
      project_id: input.projectId,
      title: input.title,
      rows: input.rows,
      totals: input.totals,
      as_of: input.asOf,
      source: 'cloud',
    })
    .select(PUB_COLUMNS);
  const rows = must(r as Res<ReflyPublication[]>);
  return rows[0]!;
}

/** Stop showing the client the list (it is kept for the record). */
export async function withdrawRefly(sb: SupabaseClient, id: string): Promise<void> {
  must((await sb.from('refly_list_publication').update({ published: false }).eq('id', id)) as Res<unknown>);
}
